import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, CircularProgress, Alert } from '@mui/material'
import { useMenu } from '../hooks/useMenu'
import { useAuth } from '../contexts/AuthContext'
import { TelaResponse } from '../types/api'
import { Folder, FileText } from 'lucide-react'

type TelaComChildren = TelaResponse & { children?: TelaComChildren[] }

/**
 * Encontra recursivamente uma tela pela rota no menu hierárquico
 */
function findTelaByRota(
  items: TelaComChildren[],
  rota: string
): TelaComChildren | null {
  const rotaNorm = rota.startsWith('/') ? rota : `/${rota}`
  for (const item of items) {
    const itemRota = item.rota?.startsWith('/') ? item.rota : `/${item.rota || ''}`
    if (itemRota === rotaNorm) return item
    if (item.children?.length) {
      const found = findTelaByRota(item.children, rota)
      if (found) return found
    }
  }
  return null
}

interface CardsFromMenuPageProps {
  /** Rota do grupo (ex: /modulo/tabelas, /g/basicas) */
  rota: string
}

/**
 * Página de cards para um nó do menu.
 *
 * Regra de exibição:
 *  - Se o pai tem filhos CARD  → exibe como cards-folha (azul sólido)
 *  - Se o pai tem filhos MENU  → exibe como folder-cards (pasta) e navega internamente
 *  - Se não tem nenhum filho   → mostra aviso
 */
const CardsFromMenuPage: React.FC<CardsFromMenuPageProps> = ({ rota }) => {
  const { getEmpresaId, isSuperAdminMode } = useAuth()
  const empresaId = getEmpresaId()
  const navigate = useNavigate()

  const { menuItems, isLoading } = useMenu(
    'GALATEA_WEB',
    !isSuperAdminMode() ? empresaId : undefined
  )

  const { telaPai, filhos, isFolderMode } = useMemo(() => {
    if (!menuItems?.length) return { telaPai: null, filhos: [] as TelaComChildren[], isFolderMode: false }

    const pai = findTelaByRota(menuItems as TelaComChildren[], rota)
    if (!pai) return { telaPai: null, filhos: [], isFolderMode: false }

    const allChildren = (pai.children || []).filter(
      c => c.ativo !== false && !c.deleted_at && c.rota
    ) as TelaComChildren[]

    // Prioridade: filhos CARD
    const cardKids = allChildren.filter(c => c.layout_tipo === 'CARD')
    if (cardKids.length > 0) {
      return { telaPai: pai, filhos: cardKids, isFolderMode: false }
    }

    // Fallback: filhos MENU (exibe como folder-cards)
    const menuKids = allChildren.filter(c => !c.layout_tipo || c.layout_tipo === 'MENU')
    return { telaPai: pai, filhos: menuKids, isFolderMode: true }
  }, [menuItems, rota])

  const handleCardClick = (tela: TelaComChildren, folderMode: boolean) => {
    const url = tela.rota?.startsWith('/') ? tela.rota : `/${tela.rota ?? ''}`
    // Folder-cards e itens MENU → navegação interna (SPA)
    if (folderMode || !tela.layout_tipo || tela.layout_tipo === 'MENU') {
      navigate(url)
    } else {
      // Folhas CARD → abre em nova aba (tela ThorJava real)
      window.open(`${window.location.origin}${url}`, '_blank', 'noopener,noreferrer')
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!telaPai) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Grupo não encontrado para a rota: {rota}
        </Alert>
      </Box>
    )
  }

  if (filhos.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {telaPai.nome}
        </Typography>
        <Alert severity="info">Nenhum item disponível neste grupo.</Alert>
      </Box>
    )
  }

  return (
    <div className="bento-section-operational p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Cabeçalho da Seção Bento */}
      <header className="section-header flex items-center justify-between pb-4 border-b border-slate-200/80 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-sky-500/10 text-[#0284C7] dark:text-sky-400 border border-sky-500/20 shadow-xs">
            {isFolderMode
              ? <Folder className="w-6 h-6" />
              : <FileText className="w-6 h-6" />}
          </div>
          <h2 className="section-title text-xl sm:text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {telaPai.nome}
          </h2>
        </div>
        <span className="section-counter bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-3.5 py-1 rounded-full text-xs font-bold font-mono border border-slate-300/50 dark:border-slate-700/50">
          ({filhos.length})
        </span>
      </header>

      {/* Grid de Cards (Bento Box Grid) */}
      <div className="card-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5">
        {filhos.map(child => (
          <MenuCard
            key={child.id}
            nome={child.nome}
            isFolder={isFolderMode}
            onClick={() => handleCardClick(child, isFolderMode)}
          />
        ))}
      </div>
    </div>
  )
}

/* ── Card individual Bento/Neumorphic Glass ──────────────────────────── */
interface MenuCardProps {
  nome: string
  isFolder: boolean
  onClick: () => void
}

const MenuCard: React.FC<MenuCardProps> = ({ nome, isFolder, onClick }) => {
  return (
    <button
      onClick={onClick}
      title={nome}
      className="action-card group relative flex flex-col items-center justify-center text-center p-5 rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1.5 shadow-sm hover:shadow-xl bg-white dark:bg-slate-800/80 backdrop-blur-md border border-slate-200/90 dark:border-slate-700/60 overflow-hidden text-slate-700 dark:text-slate-100 select-none min-h-[140px]"
    >
      {/* Efeito de Vidro Sutil (Glassmorphism overlay) */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/5 dark:from-white/5 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-2xl" />

      {/* Contêiner do Ícone Operacional (Azul Sky #0284C7) */}
      <div className="icon-container text-[#0284C7] dark:text-sky-400 mb-3 group-hover:scale-110 transition-transform duration-300 relative z-10 p-3 rounded-2xl bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-900/40 shadow-inner">
        {isFolder ? <Folder className="w-7 h-7 text-amber-500 dark:text-amber-400" /> : <FileText className="w-7 h-7 text-[#0284C7] dark:text-sky-400" />}
      </div>

      {/* Título do Cartão */}
      <div className="card-title text-xs sm:text-sm font-semibold leading-snug line-clamp-2 relative z-10">
        {nome}
      </div>
    </button>
  )
}

export default CardsFromMenuPage
